import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import {
  getAssignableUsers,
  getRegistrationLinks,
  createRegistrationLink,
  deleteRegistrationLink,
  getResetPasskeyLinks,
  createResetPasskeyLink,
  deleteResetPasskeyLink,
  getPasswordResetLinks,
  createPasswordResetLink,
  deletePasswordResetLink,
  registerUser,
} from '../lib/api';
import { getUserDisplayName } from '../lib/user';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Tabs,
  Table,
  Space,
  Typography,
  Alert,
  Tag,
  Spin,
  message,
  Divider,
} from 'antd';
import {
  ArrowLeftOutlined,
  UserAddOutlined,
  LinkOutlined,
  KeyOutlined,
  LockOutlined,
  CopyOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface RegistrationLink {
  id: number;
  token: string;
  createdBy: number;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  createdAt: string;
  isActive: boolean;
  creator: {
    name: string | null;
    email: string | null;
    username: string | null;
  };
}

interface ResetLink {
  id: number;
  token: string;
  userId: number;
  createdBy: number;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  createdAt: string;
  isActive: boolean;
  user: {
    id: number;
    email: string | null;
    username: string | null;
    name: string | null;
  };
  creator: {
    name: string | null;
  };
}

export default function CreateUser() {
  const { user } = useAuthStore();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('direct');
  const [createdUser, setCreatedUser] = useState<{
    id: number;
    email?: string;
    username: string;
    name?: string;
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [generatedResetLink, setGeneratedResetLink] = useState('');
  const [registrationLinks, setRegistrationLinks] = useState<RegistrationLink[]>([]);
  const [resetLinks, setResetLinks] = useState<ResetLink[]>([]);
  const [passwordResetLinks, setPasswordResetLinks] = useState<ResetLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linkForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [passwordResetForm] = Form.useForm();
  const [users, setUsers] = useState<{ id: number; name?: string; username?: string; email?: string }[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [generatedPasswordResetLink, setGeneratedPasswordResetLink] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'link') {
      fetchRegistrationLinks();
    } else if (activeTab === 'reset') {
      fetchResetLinks();
    } else if (activeTab === 'password-reset') {
      fetchPasswordResetLinks();
    }
  }, [activeTab]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await getAssignableUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchRegistrationLinks = async () => {
    setLinksLoading(true);
    try {
      const data = await getRegistrationLinks();
      setRegistrationLinks(data);
    } catch (err) {
      console.error('Failed to fetch registration links:', err);
    } finally {
      setLinksLoading(false);
    }
  };

  const fetchResetLinks = async () => {
    setLinksLoading(true);
    try {
      const data = await getResetPasskeyLinks();
      setResetLinks(data);
    } catch (err) {
      console.error('Failed to fetch reset links:', err);
    } finally {
      setLinksLoading(false);
    }
  };

  const fetchPasswordResetLinks = async () => {
    setLinksLoading(true);
    try {
      const data = await getPasswordResetLinks();
      setPasswordResetLinks(data);
    } catch (err) {
      console.error('Failed to fetch password reset links:', err);
    } finally {
      setLinksLoading(false);
    }
  };

  const deactivateRegistrationLink = async (tokenValue: string) => {
    try {
      await deleteRegistrationLink(tokenValue);
      setRegistrationLinks((prev) => prev.filter((link) => link.token !== tokenValue));
      message.success('已停用連結');
    } catch (err: any) {
      message.error(err.message || '停用失敗');
    }
  };

  const deactivateResetLink = async (tokenValue: string) => {
    try {
      await deleteResetPasskeyLink(tokenValue);
      setResetLinks((prev) => prev.filter((link) => link.token !== tokenValue));
      message.success('已停用連結');
    } catch (err: any) {
      message.error(err.message || '停用失敗');
    }
  };

  const deactivatePasswordResetLink = async (tokenValue: string) => {
    try {
      await deletePasswordResetLink(tokenValue);
      setPasswordResetLinks((prev) => prev.filter((link) => link.token !== tokenValue));
      message.success('已停用連結');
    } catch (err: any) {
      message.error(err.message || '停用失敗');
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setError('');

    // Validate password
    const password = values.password;
    if (!password || password.length < 8) {
      setError('密碼必須至少 8 個字符');
      setLoading(false);
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('密碼必須包含至少一個大寫英文字母');
      setLoading(false);
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError('密碼必須包含至少一個小寫英文字母');
      setLoading(false);
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError('密碼必須包含至少一個數字');
      setLoading(false);
      return;
    }

    try {
      const result = await registerUser({
        ...values,
        managerId: values.managerId ? Number(values.managerId) : undefined,
      });

      setCreatedUser({
        id: result.userId,
        email: values.email,
        username: values.username,
        name: values.name,
      });
      form.resetFields();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateRegistrationLink = async (values: any) => {
    setLoading(true);
    setError('');

    try {
      const result = await createRegistrationLink({
        maxUses: values.maxUses ? Number(values.maxUses) : null,
        expiresInHours: values.expiresInHours ? Number(values.expiresInHours) : null,
      });

      setGeneratedLink(result.link);
      fetchRegistrationLinks();
      linkForm.resetFields();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateResetLink = async (values: any) => {
    setLoading(true);
    setError('');

    try {
      const result = await createResetPasskeyLink({
        userId: Number(values.userId),
        expiresInHours: values.expiresInHours ? Number(values.expiresInHours) : null,
      });

      setGeneratedResetLink(result.link);
      message.success('重置連結已產生');
      fetchResetLinks();
      resetForm.resetFields();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generatePasswordResetLink = async (values: any) => {
    setLoading(true);
    setError('');

    try {
      const result = await createPasswordResetLink({
        userId: Number(values.userId),
        expiresInHours: values.expiresInHours ? Number(values.expiresInHours) : null,
      });

      setGeneratedPasswordResetLink(result.link);
      message.success('密碼重置連結已產生');
      fetchPasswordResetLinks();
      passwordResetForm.resetFields();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('已複製到剪貼簿');
  };

  const getFullLink = (tokenValue: string, type: 'register' | 'reset' | 'password-reset') => {
    const path = type === 'register' ? 'register' : type === 'reset' ? 'reset-passkey' : 'reset-password';
    return `${window.location.origin}/${path}?token=${tokenValue}`;
  };

  const getTimeRemaining = (dateStr: string | null) => {
    if (!dateStr) return null;
    const now = new Date();
    const expiry = new Date(dateStr);
    const diff = expiry.getTime() - now.getTime();
    if (diff <= 0) return '已過期';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `剩餘 ${days} 天`;
    }
    return `剩餘 ${hours} 小時`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '無期限';
    return new Date(dateStr).toLocaleString('zh-TW');
  };

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <Link to="/">
        <Button icon={<ArrowLeftOutlined />}>返回儀表板</Button>
      </Link>

      <Card title={<Title level={3}><UserAddOutlined /> 建立新使用者</Title>}>
        {error && (
          <Alert title={error} type="error" showIcon style={{ marginBottom: 16 }} />
        )}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'direct',
              label: (
                <span>
                  <UserAddOutlined /> 直接建立帳號
                </span>
              ),
              children: createdUser ? (
                <Alert
                  title="使用者建立成功！"
                  description={
                    <Space orientation="vertical" style={{ width: '100%' }}>
                      <Text>
                        使用者 <strong>{createdUser.username}</strong> 已成功建立。
                      </Text>
                      <Text>初始密碼已設定，請將帳號名和密碼告知該使用者。</Text>
                      <Text type="secondary">
                        使用者可以在登入後於個人設定中變更密碼或綁定 Passkey。
                      </Text>
                      <Button type="primary" onClick={() => setCreatedUser(null)}>
                        建立另一位使用者
                      </Button>
                    </Space>
                  }
                  type="success"
                  showIcon
                />
              ) : (
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                  <Form.Item
                    name="username"
                    label="帳號名"
                    rules={[
                      { required: true, message: '請輸入帳號名' },
                      {
                        pattern: /^[a-zA-Z0-9_-]+$/,
                        message: '帳號名只能包含英文、數字、底線和連字符',
                      },
                    ]}
                  >
                    <Input placeholder="用於登入的帳號名" />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    label="初始密碼"
                    rules={[{ required: true, message: '請輸入初始密碼' }]}
                  >
                    <Input.Password
                      placeholder="設定初始密碼"
                      visibilityToggle={{
                        visible: showPassword,
                        onVisibleChange: setShowPassword,
                      }}
                    />
                  </Form.Item>

                  <Alert
                    title="密碼要求"
                    description={
                      <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
                        <li>至少 8 個字符</li>
                        <li>包含大寫字母 (A-Z)</li>
                        <li>包含小寫字母 (a-z)</li>
                        <li>包含數字 (0-9)</li>
                      </ul>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <Form.Item name="name" label="姓名">
                    <Input placeholder="使用者姓名 (選填)" />
                  </Form.Item>

                  <Form.Item name="email" label="電子郵件">
                    <Input type="email" placeholder="user@example.com (選填)" />
                  </Form.Item>

                  <Form.Item name="role" label="角色" initialValue="employee">
                    <Select>
                      {user?.role === 'boss' && <Option value="boss">老闆</Option>}
                      <Option value="manager">主管</Option>
                      <Option value="employee">員工</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="managerId" label="直屬主管 ID">
                    <Input type="number" placeholder="選填" />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      建立使用者
                    </Button>
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: 'link',
              label: (
                <span>
                  <LinkOutlined /> 產生註冊連結
                </span>
              ),
              children: (
                <>
                  {generatedLink ? (
                    <Alert
                      title="註冊連結已產生！"
                      description={
                        <Space orientation="vertical" style={{ width: '100%' }}>
                          <Space.Compact style={{ width: '100%' }}>
                            <Input value={generatedLink} readOnly />
                            <Button
                              icon={<CopyOutlined />}
                              onClick={() => copyToClipboard(generatedLink)}
                            >
                              複製
                            </Button>
                          </Space.Compact>
                          <Button onClick={() => setGeneratedLink('')}>
                            產生新連結
                          </Button>
                        </Space>
                      }
                      type="success"
                      showIcon
                    />
                  ) : (
                    <Form
                      form={linkForm}
                      layout="vertical"
                      onFinish={generateRegistrationLink}
                      initialValues={{ expiresInHours: '24' }}
                    >
                      <Form.Item name="maxUses" label="使用次數限制">
                        <Input
                          type="number"
                          placeholder="留空表示無限制"
                          min={1}
                        />
                      </Form.Item>

                      <Form.Item name="expiresInHours" label="有效期限（小時）">
                        <Input type="number" min={1} />
                      </Form.Item>

                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                        >
                          產生註冊連結
                        </Button>
                      </Form.Item>
                    </Form>
                  )}

                  <Divider />

                  <Title level={5}>已發出的註冊連結</Title>
                  {linksLoading ? (
                    <Spin />
                  ) : (
                    <Table
                      dataSource={registrationLinks}
                      rowKey="id"
                      pagination={false}
                      columns={[
                        {
                          title: '創建時間',
                          dataIndex: 'createdAt',
                          render: formatDate,
                        },
                        {
                          title: '使用次數',
                          render: (_, record) =>
                            record.maxUses !== null
                              ? `${record.usedCount} / ${record.maxUses}`
                              : `${record.usedCount} / 無限制`,
                        },
                        {
                          title: '過期時間',
                          dataIndex: 'expiresAt',
                          render: (date) =>
                            date ? (
                              <Tag
                                color={
                                  new Date(date) < new Date() ? 'red' : 'green'
                                }
                              >
                                {getTimeRemaining(date)}
                              </Tag>
                            ) : (
                              '無期限'
                            ),
                        },
                        {
                          title: '操作',
                          render: (_, record) => (
                            <Space>
                              <Button
                                icon={<CopyOutlined />}
                                onClick={() =>
                                  copyToClipboard(
                                    getFullLink(record.token, 'register')
                                  )
                                }
                              >
                                複製
                              </Button>
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  deactivateRegistrationLink(record.token)
                                }
                              >
                                停用
                              </Button>
                            </Space>
                          ),
                        },
                      ]}
                    />
                  )}
                </>
              ),
            },
            {
              key: 'reset',
              label: (
                <span>
                  <KeyOutlined /> 重置 Passkey
                </span>
              ),
              children: (
                <>
                  {generatedResetLink ? (
                    <Alert
                      title="重置連結已產生！"
                      description={
                        <Space orientation="vertical" style={{ width: '100%' }}>
                          <Space.Compact style={{ width: '100%' }}>
                            <Input value={generatedResetLink} readOnly />
                            <Button
                              icon={<CopyOutlined />}
                              onClick={() =>
                                copyToClipboard(generatedResetLink)
                              }
                            >
                              複製
                            </Button>
                          </Space.Compact>
                          <Button onClick={() => setGeneratedResetLink('')}>
                            產生新連結
                          </Button>
                        </Space>
                      }
                      type="success"
                      showIcon
                    />
                  ) : (
                    <Form
                      form={resetForm}
                      layout="vertical"
                      onFinish={generateResetLink}
                      initialValues={{ expiresInHours: '24' }}
                    >
                      <Form.Item
                        name="userId"
                        label="選擇使用者"
                        rules={[{ required: true, message: '請選擇使用者' }]}
                      >
                        <Select
                          placeholder="選擇需要重置 Passkey 的使用者"
                          showSearch
                          optionFilterProp="label"
                          loading={usersLoading}
                          filterOption={(input, option) =>
                            (option?.label ?? '')
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          options={users.map((u) => ({
                            value: u.id,
                            label: `${getUserDisplayName(u)}${u.email ? ` (${u.email})` : ''}`,
                          }))}
                        />
                      </Form.Item>

                      <Form.Item name="expiresInHours" label="有效期限（小時）">
                        <Input type="number" min={1} />
                      </Form.Item>

                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          danger
                        >
                          產生重置連結
                        </Button>
                      </Form.Item>
                    </Form>
                  )}

                  <Divider />

                  <Title level={5}>已發出的重置連結</Title>
                  {linksLoading ? (
                    <Spin />
                  ) : (
                    <Table
                      dataSource={resetLinks}
                      rowKey="id"
                      pagination={false}
                      columns={[
                        {
                          title: '目標使用者',
                          render: (_, record) =>
                            record.user?.name ||
                            record.user?.username ||
                            `ID: ${record.userId}`,
                        },
                        {
                          title: '創建時間',
                          dataIndex: 'createdAt',
                          render: formatDate,
                        },
                        {
                          title: '使用狀態',
                          render: (_, record) => (
                            <Tag
                              color={record.usedCount > 0 ? 'red' : 'green'}
                            >
                              {record.usedCount > 0 ? '已使用' : '未使用'}
                            </Tag>
                          ),
                        },
                        {
                          title: '過期時間',
                          dataIndex: 'expiresAt',
                          render: (date) =>
                            date ? (
                              <Tag
                                color={
                                  new Date(date) < new Date() ? 'red' : 'green'
                                }
                              >
                                {getTimeRemaining(date)}
                              </Tag>
                            ) : (
                              '無期限'
                            ),
                        },
                        {
                          title: '操作',
                          render: (_, record) => (
                            <Space>
                              <Button
                                icon={<CopyOutlined />}
                                onClick={() =>
                                  copyToClipboard(
                                    getFullLink(record.token, 'reset')
                                  )
                                }
                              >
                                複製
                              </Button>
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  deactivateResetLink(record.token)
                                }
                              >
                                停用
                              </Button>
                            </Space>
                          ),
                        },
                      ]}
                    />
                  )}
                </>
              ),
            },
            {
              key: 'password-reset',
              label: (
                <span>
                  <LockOutlined /> 重置密碼
                </span>
              ),
              children: (
                <>
                  {generatedPasswordResetLink ? (
                    <Alert
                      title="密碼重置連結已產生！"
                      description={
                        <Space orientation="vertical" style={{ width: '100%' }}>
                          <Space.Compact style={{ width: '100%' }}>
                            <Input value={generatedPasswordResetLink} readOnly />
                            <Button
                              icon={<CopyOutlined />}
                              onClick={() =>
                                copyToClipboard(generatedPasswordResetLink)
                              }
                            >
                              複製
                            </Button>
                          </Space.Compact>
                          <Button
                            onClick={() => setGeneratedPasswordResetLink('')}
                          >
                            產生新連結
                          </Button>
                        </Space>
                      }
                      type="success"
                      showIcon
                    />
                  ) : (
                    <Form
                      form={passwordResetForm}
                      layout="vertical"
                      onFinish={generatePasswordResetLink}
                      initialValues={{ expiresInHours: '24' }}
                    >
                      <Form.Item
                        name="userId"
                        label="選擇使用者"
                        rules={[{ required: true, message: '請選擇使用者' }]}
                      >
                        <Select
                          placeholder="選擇需要重置密碼的使用者"
                          showSearch
                          optionFilterProp="label"
                          loading={usersLoading}
                          filterOption={(input, option) =>
                            (option?.label ?? '')
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          options={users.map((u) => ({
                            value: u.id,
                            label: `${getUserDisplayName(u)}${u.email ? ` (${u.email})` : ''}`,
                          }))}
                        />
                      </Form.Item>

                      <Form.Item name="expiresInHours" label="有效期限（小時）">
                        <Input type="number" min={1} />
                      </Form.Item>

                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          danger
                        >
                          產生密碼重置連結
                        </Button>
                      </Form.Item>
                    </Form>
                  )}

                  <Divider />

                  <Title level={5}>已發出的密碼重置連結</Title>
                  {linksLoading ? (
                    <Spin />
                  ) : (
                    <Table
                      dataSource={passwordResetLinks}
                      rowKey="id"
                      pagination={false}
                      columns={[
                        {
                          title: '目標使用者',
                          render: (_, record) =>
                            record.user?.name ||
                            record.user?.username ||
                            `ID: ${record.userId}`,
                        },
                        {
                          title: '創建時間',
                          dataIndex: 'createdAt',
                          render: formatDate,
                        },
                        {
                          title: '使用狀態',
                          render: (_, record) => (
                            <Tag
                              color={record.usedCount > 0 ? 'red' : 'green'}
                            >
                              {record.usedCount > 0 ? '已使用' : '未使用'}
                            </Tag>
                          ),
                        },
                        {
                          title: '過期時間',
                          dataIndex: 'expiresAt',
                          render: (date) =>
                            date ? (
                              <Tag
                                color={
                                  new Date(date) < new Date() ? 'red' : 'green'
                                }
                              >
                                {getTimeRemaining(date)}
                              </Tag>
                            ) : (
                              '無期限'
                            ),
                        },
                        {
                          title: '操作',
                          render: (_, record) => (
                            <Space>
                              <Button
                                icon={<CopyOutlined />}
                                onClick={() =>
                                  copyToClipboard(
                                    getFullLink(record.token, 'password-reset')
                                  )
                                }
                              >
                                複製
                              </Button>
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  deactivatePasswordResetLink(record.token)
                                }
                              >
                                停用
                              </Button>
                            </Space>
                          ),
                        },
                      ]}
                    />
                  )}
                </>
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );
}
